/**
 * 创作人管理（07 功能 PRD §3.1）：列表（状态筛选）→ 编辑（双语表单/照片/npcPos/排序）
 * → 预览（前台 /builders 波轮直达）→ 发布/下线/删除。
 * 增人流程验收：发布后人物 ≤1 分钟出现在首页场景与波轮（公开 API 60s TTL 兜底）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type {
  AdminBuilderDetail,
  AdminBuilderSummary,
  SocialLinkInput,
} from '@chronospeak/contracts';
import { socialPlatforms, builderSortModes } from '@chronospeak/contracts';
import type { BuilderSortMode } from '@chronospeak/contracts';
import { adminHttp, ApiError } from '../lib/api';
import { uploadFile } from '../lib/upload';
import { Button } from '@chronospeak/ui';
import { ErrorNote, Loading, PageHeader, StatusBadge, TableWrap } from '../components/ui-bits';
import { TranslateButton, MachineDraftBadge } from '../components/TranslateButton';
import { useSession } from '../lib/session';

/* ---------------- 列表（行拖拽排序：2026-09-24 用户需求） ---------------- */

/** name_* 模式本地预览排序（与公开投影同一 Intl.Collator 规则；slug tiebreak） */
function previewSort(items: AdminBuilderSummary[], mode: BuilderSortMode): AdminBuilderSummary[] {
  const dir = mode === 'name_asc' ? 1 : -1;
  const collator = new Intl.Collator('zh-Hans-CN', { sensitivity: 'base' });
  return [...items].sort((a, b) => dir * collator.compare(a.name, b.name) || a.slug.localeCompare(b.slug));
}

export function BuildersListPage() {
  const [data, setData] = useState<{ items: AdminBuilderSummary[]; total: number } | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  // 拖拽排序本地态：rows 为当前展示顺序；dirty=与库中 sortOrder 有差异待保存
  const [rows, setRows] = useState<AdminBuilderSummary[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  // 排序模式（2026-09-25）：manual=可拖拽；name_* 只读预览（服务端投影语义与本地排序一致）
  const [sortMode, setSortMode] = useState<BuilderSortMode>('manual');
  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setDirty(false);
    try {
      const res = await adminHttp.get<{ items: AdminBuilderSummary[]; total: number }>(
        `/admin/builders?pageSize=100${status ? `&status=${status}` : ''}`,
      );
      setData(res);
      // 当前排序模式（settings 单键读取）→ name_* 预览与公开投影同规则（Intl.Collator 拼音序）
      let mode: BuilderSortMode = 'manual';
      try {
        const settings = await adminHttp.get<{ items: { key: string; value: string }[] }>('/admin/settings');
        const v = settings.items.find((i) => i.key === 'builders.sortMode')?.value;
        if (v && (builderSortModes as readonly string[]).includes(v)) mode = v as BuilderSortMode;
      } catch {
        // settings 读取失败不阻塞列表——回落 manual
      }
      setSortMode(mode);
      setRows(mode === 'manual' ? res.items : previewSort(res.items, mode));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 行拖拽：dragstart 记起点，dragover 目标位插入，drop 后置脏标记 */
  const moveRow = (from: number, to: number) => {
    setRows((prev) => {
      if (!prev || from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
    setDirty(true);
  };

  /** 排序模式切换：PATCH settings + 本地预览重排；name_* 下拖拽禁用 */
  const changeSortMode = async (mode: BuilderSortMode) => {
    setError(null);
    try {
      await adminHttp.patch('/admin/settings', {
        items: [{ key: 'builders.sortMode', value: mode }],
      });
      setSortMode(mode);
      setRows((prev) => (prev ? (mode === 'manual' ? [...prev].sort((a, b) => a.sortOrder - b.sortOrder) : previewSort(prev, mode)) : prev));
      setNotice(
        mode === 'manual'
          ? '已切回手动排序（拖拽可调整，保存后生效）。'
          : '已切换为姓名排序（访客端即时生效；切回手动可恢复原拖拽顺序）。',
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '排序模式切换失败');
    }
  };

  const saveOrder = async () => {
    if (!rows) return;
    setSavingOrder(true);
    setError(null);
    try {
      await adminHttp.post('/admin/builders/reorder', {
        items: rows.map((r, i) => ({ id: r.id, sortOrder: i })),
      });
      setNotice('排序已保存（前台 ≤1 分钟生效）。');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '排序保存失败');
    } finally {
      setSavingOrder(false);
    }
  };

  const [notice, setNotice] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    try {
      const created = await adminHttp.post<AdminBuilderDetail>('/admin/builders', {
        slug: `builder-${Date.now().toString(36)}`,
        translations: { 'zh-CN': { name: '新创作人', role: '创作人', statement: '', bio: '' } },
        sortOrder: 99,
      });
      navigate(`/builders/${created.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '创建失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <PageHeader
        title='创作人管理'
        sub='首页人物与波轮共用此数据；发布后 ≤1 分钟在前台生效。拖动行首 ☰ 可调整顺序。'
        actions={
          <>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
              排序模式{' '}
              <select
                value={sortMode}
                onChange={(e) => void changeSortMode(e.target.value as BuilderSortMode)}
                className='mgr-select'
                aria-label='创作人排序模式'
              >
                <option value='manual'>手动排序（拖拽）</option>
                <option value='name_asc'>按姓名 A→Z</option>
                <option value='name_desc'>按姓名 Z→A</option>
              </select>
            </label>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
              状态{' '}
              <select value={status} onChange={(e) => setStatus(e.target.value)} className='mgr-select'>
                <option value=''>全部</option>
                <option value='DRAFT'>草稿</option>
                <option value='PUBLISHED'>已发布</option>
                <option value='OFFLINE'>已下线</option>
              </select>
            </label>
            <Button variant='primary' size='md' onClick={() => void create()} disabled={busy}>
              新建创作人
            </Button>
          </>
        }
      />
      {error && <ErrorNote message={error} />}
      {notice && !error && (
        <p role='status' className='mgr-notice'>
          {notice}
        </p>
      )}
      {!data && !error && <Loading />}
      {rows && (
        <>
          {sortMode !== 'manual' && (
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 10px' }} role='status'>
              当前为姓名自动排序（访客端已生效）；拖拽已禁用——切回「手动排序（拖拽）」可恢复拖拽调整。
            </p>
          )}
          {dirty && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0 0 10px' }}>
              <Button variant='primary' size='sm' onClick={() => void saveOrder()} disabled={savingOrder}>
                {savingOrder ? '保存中…' : '保存顺序'}
              </Button>
              <Button variant='ghost' size='sm' onClick={() => void load()} disabled={savingOrder}>
                放弃改动
              </Button>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>顺序有未保存改动</span>
            </div>
          )}
          <TableWrap>
            <thead>
              <tr>
                <th scope='col' style={{ width: 56 }}>
                  排序
                </th>
                <th scope='col'>slug</th>
                <th scope='col'>姓名</th>
                <th scope='col'>状态</th>
                <th scope='col'>照片</th>
                <th scope='col'>双语</th>
                <th scope='col'>更新时间</th>
                <th scope='col' />
              </tr>
            </thead>
            <tbody>
              {rows.map((b, i) => (
                <tr
                  key={b.id}
                  draggable={sortMode === 'manual'}
                  onDragStart={(e) => {
                    dragIndex.current = i;
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox 需要 setData 才启动拖拽
                    e.dataTransfer.setData('text/plain', b.id);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault(); // 允许 drop
                    setDragOverIndex(i);
                  }}
                  onDragLeave={() => setDragOverIndex((cur) => (cur === i ? null : cur))}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex.current !== null && dragIndex.current !== i) moveRow(dragIndex.current, i);
                    dragIndex.current = null;
                    setDragOverIndex(null);
                  }}
                  onDragEnd={() => {
                    dragIndex.current = null;
                    setDragOverIndex(null);
                  }}
                  style={{
                    ...(dragOverIndex === i && dragIndex.current !== null && dragIndex.current !== i
                      ? { boxShadow: 'inset 0 2px 0 var(--gold)' }
                      : {}),
                    cursor: 'grab',
                  }}
                >
                  <td>
                    <span
                      aria-hidden='true'
                      style={{ marginRight: 6, color: 'var(--muted)', userSelect: 'none', opacity: sortMode === 'manual' ? 1 : 0.35, cursor: sortMode === 'manual' ? 'grab' : 'not-allowed' }}
                    >
                      ☰
                    </span>
                    {b.sortOrder}
                  </td>
                  <td>
                    <code>{b.slug}</code>
                  </td>
                  <td>{b.name}</td>
                  <td>
                    <StatusBadge status={b.status} />
                  </td>
                  <td>{b.hasPhoto ? '✓' : '—'}</td>
                  <td>{b.hasTranslation ? 'zh+en' : 'zh'}</td>
                  <td>{new Date(b.updatedAt).toLocaleString('zh-CN')}</td>
                  <td>
                    <Button variant='ghost' size='sm' onClick={() => navigate(`/builders/${b.id}`)}>
                      编辑
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </>
      )}
    </section>
  );
}

/* ---------------- 编辑 ---------------- */

interface FormState {
  slug: string;
  zh: { name: string; role: string; statement: string; bio: string };
  en: { name: string; role: string; statement: string; bio: string };
  hasEn: boolean;
  photoAssetKey: string | null;
  photoUrl: string | null;
  avatar: number | null;
  npcX: string;
  npcY: string;
  isCoach: boolean;
  sortOrder: string;
  status: string;
  /** 社媒跳转项（≤3；服务端全量替换——表单始终显式提交终态） */
  socialLinks: SocialRow[];
}

/** 平台选项（socialPlatforms 枚举 + 中文展示名；02 实现说明 §3 下拉） */
const SOCIAL_PLATFORM_OPTIONS: { value: string; label: string }[] = [
  { value: 'wechat', label: '微信公众号' },
  { value: 'xiaohongshu', label: '小红书' },
  { value: 'shipinhao', label: '视频号' },
  { value: 'douyin', label: '抖音' },
  { value: 'bilibili', label: 'Bilibili' },
  { value: 'other', label: '其他' },
];

/** 编辑页社媒行内部形态：id 仅前端 key 用（回显时取 detail.id，新行本地生成），提交时剥掉 */
interface SocialRow extends SocialLinkInput {
  _key: string;
}

export function BuilderEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { admin } = useSession();
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  /** 「机翻初稿」脏标记：机翻填充置 true，任一英文字段编辑后清 false */
  const [mtDraft, setMtDraft] = useState(false);

  useEffect(() => {
    adminHttp
      .get<AdminBuilderDetail>(`/admin/builders/${id}`)
      .then((d) =>
        setForm({
          slug: d.slug,
          zh: d.translations['zh-CN'],
          en: d.translations.en ?? { name: '', role: '', statement: '', bio: '' },
          hasEn: Boolean(d.translations.en),
          photoAssetKey: d.photoAssetKey,
          photoUrl: d.photoUrl,
          avatar: d.avatar,
          npcX: d.npcPos ? String(Math.round(d.npcPos.x)) : '',
          npcY: d.npcPos ? String(Math.round(d.npcPos.y)) : '',
          isCoach: d.isCoach,
          sortOrder: String(d.sortOrder),
          status: d.status,
          socialLinks: (d.socialLinks ?? []).map((l, i) => ({
            _key: l.id,
            platform: l.platform,
            label: l.label,
            url: l.url,
            imageUrl: l.imageUrl,
            sortOrder: l.sortOrder ?? i,
          })),
        }),
      )
      .catch((e) => setError(e instanceof ApiError ? e.message : '加载失败'));
  }, [id]);

  if (error && !form) {
    return (
      <section>
        <PageHeader title='编辑创作人' />
        <ErrorNote message={error} />
      </section>
    );
  }
  if (!form) {
    return (
      <section>
        <PageHeader title='编辑创作人' />
        <Loading />
      </section>
    );
  }

  const patch = (p: Partial<FormState>) => setForm({ ...form, ...p });

  const save = async () => {
    setError(null);
    setNotice(null);
    // 前端即时校验（07：格式与取值后台校验）
    if (!form.zh.name.trim()) return setError('姓名（中文）必填。');
    if (!form.zh.role.trim()) return setError('身份/方向（中文）必填。');
    const x = form.npcX === '' ? null : Number(form.npcX);
    const y = form.npcY === '' ? null : Number(form.npcY);
    if ((x !== null && (!Number.isFinite(x) || x < 0 || x > 1652)) || (y !== null && (!Number.isFinite(y) || y < 0 || y > 925))) {
      return setError('npcPos 超出地面坐标系范围（x 0–1652 / y 0–925）。');
    }
    const sortOrder = Number(form.sortOrder);
    if (!Number.isInteger(sortOrder)) return setError('排序必须是整数。');
    // 社媒即时校验（服务端仍会 422 兜底——前端限制可被绕过，但即时反馈更友好）
    for (const [i, link] of form.socialLinks.entries()) {
      if (!link.platform) return setError(`社媒第 ${i + 1} 行：请先选择平台。`);
      if (!/^https?:\/\//i.test(link.url.trim())) {
        return setError(`社媒第 ${i + 1} 行：链接必须是 http/https 开头的完整网址。`);
      }
    }
    try {
      await adminHttp.patch<AdminBuilderDetail>(`/admin/builders/${id}`, {
        slug: form.slug.trim(),
        translations: {
          'zh-CN': form.zh,
          ...(form.hasEn && form.en.name.trim() ? { en: form.en } : {}),
        },
        photoAssetKey: form.photoAssetKey,
        avatar: form.avatar,
        npcPos: x !== null && y !== null ? { x, y } : null,
        isCoach: form.isCoach,
        sortOrder,
        // 全量替换终态：_key 为前端 key，服务端契约只收 SocialLinkInput 字段
        socialLinks: form.socialLinks.map(({ _key: _ignored, ...rest }, i) => ({
          ...rest,
          url: rest.url.trim(),
          sortOrder: i,
        })),
      });
      setNotice('已保存。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '保存失败');
    }
  };

  const doPublish = async (action: 'publish' | 'unpublish') => {
    setError(null);
    try {
      const res = await adminHttp.post<{ status: string }>(`/admin/builders/${id}/${action}`);
      patch({ status: res.status });
      setNotice(action === 'publish' ? '已发布（前台 ≤1 分钟生效）。' : '已下线（前台 ≤1 分钟消失）。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '操作失败');
    }
  };

  const doDelete = async () => {
    if (!window.confirm('删除草稿不可恢复，确定？')) return;
    try {
      await adminHttp.delete(`/admin/builders/${id}`);
      navigate('/builders');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '删除失败（已发布请走下线）');
    }
  };

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const key = await uploadFile('builder-photo', file);
      patch({ photoAssetKey: key, photoUrl: `/api/v1/assets/${encodeURIComponent(key)}` });
      setNotice('照片已上传，保存后生效。');
    } catch (e) {
      setError(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  /* ---------------- 社媒跳转区块（2026-09-23 FR-03 增补） ---------------- */

  const addSocialRow = () => {
    if (form.socialLinks.length >= 3) return;
    patch({
      socialLinks: [
        ...form.socialLinks,
        { _key: `new-${Date.now().toString(36)}`, platform: 'wechat', label: '微信公众号', url: '', imageUrl: null, sortOrder: form.socialLinks.length },
      ],
    });
  };

  const patchSocialRow = (idx: number, p: Partial<SocialRow>) => {
    patch({
      socialLinks: form.socialLinks.map((row, i) => (i === idx ? { ...row, ...p } : row)),
    });
  };

  const removeSocialRow = (idx: number) => {
    patch({ socialLinks: form.socialLinks.filter((_, i) => i !== idx) });
  };

  /** 名片二维码上传（可选；presign purpose=social-qr，≤1MB png/jpeg/webp） */
  const onSocialQr = async (idx: number, file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const key = await uploadFile('social-qr', file);
      patchSocialRow(idx, { imageUrl: key });
      setNotice('二维码已上传，保存后生效。');
    } catch (e) {
      setError(e instanceof Error ? e.message : '二维码上传失败');
    } finally {
      setUploading(false);
    }
  };

  const socialFull = form.socialLinks.length >= 3;

  const published = form.status === 'PUBLISHED';

  return (
    <section>
      <PageHeader
        title='编辑创作人'
        sub={<>状态 <StatusBadge status={form.status} /> · slug <code>{form.slug}</code></>}
        actions={
          <>
            {published && (
              <a className='mgr-preview-link' href={`/api/v1/public/builders?locale=zh-CN`} target='_blank' rel='noreferrer'>
                预览数据
              </a>
            )}
            {form.status === 'DRAFT' && (
              <Button variant='ghost' size='sm' onClick={() => void doDelete()}>
                删除草稿
              </Button>
            )}
            {published ? (
              <Button variant='ghost' size='sm' onClick={() => void doPublish('unpublish')}>
                下线
              </Button>
            ) : (
              <Button variant='primary' size='sm' onClick={() => void doPublish('publish')}>
                发布
              </Button>
            )}
            <Button variant='primary' size='sm' onClick={() => void save()}>
              保存
            </Button>
          </>
        }
      />
      {error && <ErrorNote message={error} />}
      {notice && (
        <p role='status' className='mgr-notice'>
          {notice}
        </p>
      )}

      <div className='mgr-form-grid'>
        <fieldset className='mgr-fieldset'>
          <legend>中文（必填）</legend>
          <label className='mgr-field'>
            姓名 *<input value={form.zh.name} onChange={(e) => patch({ zh: { ...form.zh, name: e.target.value } })} />
          </label>
          <label className='mgr-field'>
            身份/方向 *<input value={form.zh.role} onChange={(e) => patch({ zh: { ...form.zh, role: e.target.value } })} />
          </label>
          <label className='mgr-field'>
            宣言（一句话，工牌/波轮介绍区）
            <textarea rows={2} value={form.zh.statement} onChange={(e) => patch({ zh: { ...form.zh, statement: e.target.value } })} />
          </label>
          <label className='mgr-field'>
            简介（支持换行、**加粗**、*斜体*、[链接](https://…)）
            <textarea
              rows={4}
              value={form.zh.bio}
              placeholder={'支持换行与基础格式：\n**加粗** · *斜体* · [链接文字](https://…)**\n空行分段。'}
              onChange={(e) => patch({ zh: { ...form.zh, bio: e.target.value } })}
            />
          </label>
        </fieldset>
        <fieldset className='mgr-fieldset'>
          <legend>
            <label style={{ display: 'inline', gap: 6 }}>
              <input type='checkbox' checked={form.hasEn} onChange={(e) => patch({ hasEn: e.target.checked })} /> 英文
              （缺省前台回退中文）
            </label>
            <MachineDraftBadge visible={mtDraft} />
          </legend>
          {form.hasEn && (
            <>
              <div style={{ marginBottom: 12 }}>
                <TranslateButton
                  mtEnabled={admin?.mtEnabled ?? false}
                  collectSources={() => [form.zh.name, form.zh.role, form.zh.statement, form.zh.bio]}
                  onBeforeApply={() =>
                    [form.en.name, form.en.role, form.en.statement, form.en.bio].every((v) => !v.trim()) ||
                    window.confirm('英文区已有内容，确定用机器翻译结果覆盖？')
                  }
                  onApply={(translated) => {
                    const [name = '', role = '', statement = '', bio = ''] = translated;
                    patch({ en: { name, role, statement, bio } });
                    setMtDraft(true);
                  }}
                />
              </div>
              <label className='mgr-field'>
                Name<input value={form.en.name} onChange={(e) => { patch({ en: { ...form.en, name: e.target.value } }); setMtDraft(false); }} />
              </label>
              <label className='mgr-field'>
                Role<input value={form.en.role} onChange={(e) => { patch({ en: { ...form.en, role: e.target.value } }); setMtDraft(false); }} />
              </label>
              <label className='mgr-field'>
                Statement
                <textarea rows={2} value={form.en.statement} onChange={(e) => { patch({ en: { ...form.en, statement: e.target.value } }); setMtDraft(false); }} />
              </label>
              <label className='mgr-field'>
                Bio（line breaks, **bold**, *italic*, [link](https://…)）
                <textarea
                  rows={4}
                  value={form.en.bio}
                  placeholder={'Line breaks + basic format:\n**bold** · *italic* · [link text](https://…)**\nBlank line starts a new paragraph.'}
                  onChange={(e) => { patch({ en: { ...form.en, bio: e.target.value } }); setMtDraft(false); }}
                />
              </label>
            </>
          )}
        </fieldset>
      </div>

      <div className='mgr-form-grid'>
        <fieldset className='mgr-fieldset'>
          <legend>人物照片（单张；工牌贴图前台按记录自动合成）</legend>
          {form.photoUrl && (
            <img src={form.photoUrl} alt='人物照片预览' style={{ maxWidth: 160, borderRadius: 8, display: 'block', marginBottom: 8 }} />
          )}
          <input type='file' accept='image/png,image/jpeg,image/webp' onChange={(e) => void onPhoto(e.target.files?.[0])} disabled={uploading} />
          {uploading && <Loading text='上传中…' />}
          {form.photoAssetKey && (
            <Button variant='ghost' size='sm' onClick={() => patch({ photoAssetKey: null, photoUrl: null })}>
              移除照片（回退占位插画）
            </Button>
          )}
          {!form.photoAssetKey && (
            <label className='mgr-field'>
              占位插画编号（1–6，无照片时前台回退）
              <input
                type='number'
                min={1}
                max={6}
                value={form.avatar ?? ''}
                onChange={(e) => patch({ avatar: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </label>
          )}
        </fieldset>
        <fieldset className='mgr-fieldset'>
          <legend>身份标记</legend>
          <label className='mgr-field' style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type='checkbox'
              checked={form.isCoach}
              onChange={(e) => patch({ isCoach: e.target.checked })}
            />
            <span>
              Coach（前台波轮介绍区身份右侧显示 coach 胶囊 tag）
            </span>
          </label>
        </fieldset>
        <fieldset className='mgr-fieldset'>
          <legend>首页位置与排序</legend>
          <label className='mgr-field'>
            npcPos x（地面坐标系 0–1652）
            <input value={form.npcX} onChange={(e) => patch({ npcX: e.target.value })} inputMode='numeric' />
          </label>
          <label className='mgr-field'>
            npcPos y（0–925）
            <input value={form.npcY} onChange={(e) => patch({ npcY: e.target.value })} inputMode='numeric' />
          </label>
          <label className='mgr-field'>
            排序（决定波轮顺序）
            <input value={form.sortOrder} onChange={(e) => patch({ sortOrder: e.target.value })} inputMode='numeric' />
          </label>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 0' }}>
            地图可视化点选器为 P1；坐标可从首页小镇图按比例估算。
          </p>
        </fieldset>
      </div>

      <fieldset className='mgr-fieldset'>
        <legend>
          社交媒体（波轮介绍区展示，最多 3 个平台）
        </legend>
        <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px' }}>
          按钮图标恒为内置平台图标，无需上传；下方图片为<strong>可选</strong>的名片二维码——桌面端点击该平台时弹出展示。
        </p>
        {form.socialLinks.map((row, i) => (
          <div
            key={row._key}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(140px, 200px) minmax(200px, 1fr) auto',
              gap: 8,
              alignItems: 'start',
              borderTop: i > 0 ? '1px solid var(--line)' : undefined,
              paddingTop: i > 0 ? 10 : 0,
              marginTop: i > 0 ? 10 : 0,
            }}
          >
            <label className='mgr-field'>
              平台
              <select
                value={row.platform}
                onChange={(e) => {
                  const opt = SOCIAL_PLATFORM_OPTIONS.find((o) => o.value === e.target.value);
                  patchSocialRow(i, { platform: e.target.value, label: opt?.label ?? row.label });
                }}
              >
                {SOCIAL_PLATFORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className='mgr-field'>
              跳转链接（http/https）
              <input
                value={row.url}
                onChange={(e) => patchSocialRow(i, { url: e.target.value })}
                placeholder='https://…'
                inputMode='url'
              />
            </label>
            <div className='mgr-field' style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>可选二维码</span>
              {row.imageUrl && (
                <img
                  src={`/api/v1/assets/${encodeURIComponent(row.imageUrl)}`}
                  alt='名片二维码预览'
                  style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--line)' }}
                />
              )}
              <input
                type='file'
                accept='image/png,image/jpeg,image/webp'
                onChange={(e) => void onSocialQr(i, e.target.files?.[0])}
                disabled={uploading}
                style={{ maxWidth: 210, fontSize: 12 }}
                aria-label={`第 ${i + 1} 行名片二维码上传`}
              />
              {row.imageUrl && (
                <Button variant='ghost' size='sm' onClick={() => patchSocialRow(i, { imageUrl: null })}>
                  移除图
                </Button>
              )}
              <Button variant='ghost' size='sm' onClick={() => removeSocialRow(i)}>
                删除行
              </Button>
            </div>
          </div>
        ))}
        {socialFull ? (
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '8px 0 0' }} role='status'>
            已达 3 个平台上限，如需更换请先删除一行。
          </p>
        ) : (
          <Button variant='ghost' size='sm' onClick={addSocialRow}>
            + 添加平台
          </Button>
        )}
      </fieldset>
    </section>
  );
}
